import { CopyButton } from '@src/components/CopyButton';
import { InlineFormattedNumber } from '@src/components/InlineFormattedNumber';
import type { CustomerBalanceTransaction, CustomerOrder } from '@talos/kyoko';
import {
  CustomerBalanceTransactionStatusEnum,
  CustomerBalanceTransactionTypeEnum,
  formattedDate,
  OrdStatusEnum,
  SideEnum,
  toBigWithDefault,
  useBalanceTransactionsContext,
  useConstant,
  useObservableValue,
  useWLOrdersProvider,
} from '@talos/kyoko';
import { useLocation } from 'react-router-dom';
import { map, of } from 'rxjs';

export function EntityDetailsPage() {
  const location = useLocation();
  // useConstant is used to get the initial location, since unmounting causes blank page to be animated out
  const initialLocation = useConstant(location);
  const urlSearchParams = new URLSearchParams(initialLocation.search);
  const orderID = urlSearchParams.get('orderID') || undefined;
  const transactionID = urlSearchParams.get('transactionID') || undefined;

  return (
    <div className="p-4" data-testid="entity-details-page">
      <EntityDetailsPageInner orderID={orderID!} transactionID={transactionID} />
    </div>
  );
}

function EntityDetailsPageInner({ orderID, transactionID }: { orderID?: string; transactionID?: string }) {
  const { recentOrdersByOrdID } = useWLOrdersProvider();
  const { transactionsByIdObs } = useBalanceTransactionsContext();

  const transaction = useObservableValue(() => {
    return !transactionID
      ? of(undefined)
      : transactionsByIdObs.pipe(
          map(transactionsMap => {
            return transactionID ? transactionsMap.get(transactionID) : undefined;
          })
        );
  }, [transactionID, transactionsByIdObs]);

  if (orderID) {
    const order = recentOrdersByOrdID?.get(orderID);
    // TODO(robertz): improve by adding loading that shows after 1 second, then show a 404 after 5 seconds?
    if (!order) {
      // improve this by providing a 404 page after loading...
      return <>Order not found</>;
    }
    // Render the order details page
    return <OrderDetailsPage order={order} />;
  }
  if (transactionID) {
    // TODO(robertz): improve by adding loading that shows after 1 second, then show a 404 after 5 seconds?
    if (transaction) {
      // Render the transaction details page
      return <TransactionDetailsPage transaction={transaction} navigatedFromPlacement={false} />;
    }
    return <>Transaction not found</>;
  }
  return null;
}

export function OrderDetailsPage({ order }: { order: CustomerOrder }) {
  const quantity =
    order.Side === SideEnum.Buy ? (
      <InlineFormattedNumber currency={order.Currency} number={order.CumQty} />
    ) : (
      <InlineFormattedNumber currency={order.AmountCurrency} number={order.CumAmt} />
    );

  const amount =
    order.Side === SideEnum.Buy ? (
      <InlineFormattedNumber currency={order.AmountCurrency} number={order.CumAmt} />
    ) : (
      <InlineFormattedNumber currency={order.Currency} number={order.CumQty} />
    );

  const isReject =
    order.OrdStatus === OrdStatusEnum.Rejected ||
    (order.OrdStatus === OrdStatusEnum.Canceled && toBigWithDefault(order.CumQty, 0).eq(0));

  return (
    <div className="bg-color-bg-main w-full rounded-xl" style={{ boxShadow: '0 0 4px 0 rgba(232, 238, 242, 0.79)' }}>
      <div className="border-color-border flex w-full flex-col items-center gap-2 border-b p-4">
        <img
          src={isReject ? '/failure.png' : '/success.png'}
          alt="Status icon"
          className="mx-auto aspect-square w-1/3 max-w-[200px]"
        />
        <p
          data-testid="trade-result"
          className={`text-2xl font-semibold ${isReject ? 'text-color-text-error' : 'text-color-bg-primary'}`}
        >
          Your trade is {isReject ? 'unsuccessful' : 'successful'}
        </p>
        <p data-testid="confirmation-header-quantity" className="text-center text-3xl font-bold text-gray-800">
          {quantity}
        </p>
      </div>

      {/* Details */}
      <div className="text-left">
        <div className="border-color-border flex flex-col gap-1 border-b p-4">
          <div className="flex justify-between">
            <span className="text-color-text-weak flex-1">Quantity</span>
            <span
              data-testid="confirmation-quantity"
              className="flex w-full flex-1 items-end justify-end text-right font-semibold"
            >
              {quantity}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-color-text-weak flex-1">Amount</span>
            <span
              data-testid="confirmation-amount"
              className="flex w-full flex-1 items-end justify-end text-right font-semibold"
            >
              {amount}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-color-text-weak flex-1">Date & Time</span>
            <span className="text-color-text-weak flex w-full flex-1 items-end justify-end text-right font-semibold">
              {formattedDate(order.Timestamp)}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1 p-4">
          {!toBigWithDefault(order.AvgPx, 0).eq(0) && (
            <div className="flex justify-between">
              <span className="text-color-text-weak flex-1">Execution Price</span>
              <span
                data-testid="confirmation-execution-price"
                className="flex w-full flex-1 items-end justify-end text-right font-semibold"
              >
                <InlineFormattedNumber currency={order.AmountCurrency} number={order.AvgPx} />
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-color-text-weak flex-1">Transaction ID</span>
            <span className="text-right">
              <CopyButton value={order.OrderID} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TransactionDetailsPage({
  transaction,
  navigatedFromPlacement = true,
}: {
  transaction: CustomerBalanceTransaction;
  navigatedFromPlacement: boolean;
}) {
  const isSuccess = transaction?.Status === CustomerBalanceTransactionStatusEnum.Completed;

  if (!transaction) {
    return null;
  }

  const isTopup = transaction.TransactionType === CustomerBalanceTransactionTypeEnum.Deposit;
  const isWithdraw = transaction.TransactionType === CustomerBalanceTransactionTypeEnum.Withdrawal;

  return (
    <div className="border-color-border bg-color-bg-main w-full rounded-xl border">
      <div className="border-color-border flex w-full flex-col items-center gap-2 border-b p-4">
        <img
          src={!isSuccess ? '/failure.png' : '/success.png'}
          alt="Status icon"
          className="mx-auto aspect-square w-1/3 max-w-[200px]"
        />
        <p className={`text-lg font-semibold ${!isSuccess ? 'text-color-text-error' : 'text-color-bg-primary'}`}>
          {`Your ${isTopup ? 'top up' : 'withdrawal'} is ${!isSuccess ? 'unsuccessful' : 'successful'}`}
        </p>
        {navigatedFromPlacement && (
          <>
            {isTopup && (
              <>
                {!isSuccess && (
                  <p>We couldn&apos;t complete your top-up. Please try again or reach out to the GCash Help Center.</p>
                )}
                {isSuccess && <p>Your account will have additional balance amounting to:</p>}
              </>
            )}
            {isWithdraw && (
              <>
                {!isSuccess && (
                  <p>
                    We couldn&apos;t complete your withdrawal. Please try again or reach out to the GCash Help Center.
                  </p>
                )}
                {isSuccess && <p>Your GCash wallet will have additional balance amounting to:</p>}
              </>
            )}
          </>
        )}
        {isSuccess && (
          <span className="text-center text-2xl font-bold text-gray-800">
            <InlineFormattedNumber number={transaction.Quantity} currency={transaction.Currency} />
          </span>
        )}
      </div>

      {/* Details */}
      <div className="text-left">
        <div className="flex flex-col gap-1 p-4">
          <div className="flex justify-between">
            <span className="text-color-text-weak flex-1">Date & Time</span>
            <span className="text-color-text-weak flex w-full flex-1 items-end justify-end text-right font-semibold">
              {formattedDate(transaction.Timestamp)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-color-text-weak flex-1">Transaction ID</span>
            <span className="flex w-full flex-1 items-end justify-end text-right font-semibold">
              <CopyButton value={transaction.TransactionID} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
